import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { Check, Flag, Inbox, ShieldCheck, X } from "lucide-solid";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@famidex/shared";
import { useI18n } from "@/providers/i18n-provider";
import { showToast } from "@/components/ui/toast";

type ErrorModel = components["schemas"]["ErrorModel"];
type ReportListResponse = components["schemas"]["ReportListResponse"];
type Report = components["schemas"]["Report"];

type StatusFilter = "pending" | "resolved" | "dismissed";

const FILTERS: StatusFilter[] = ["pending", "resolved", "dismissed"];

/**
 * "Gabinete del Guardián" — la cola de moderación del back-office. Lee los
 * reportes de contenido que crea la comunidad (W1-COMMUNITY) y los resuelve:
 * `Resolver` (acción tomada) o `Descartar` (sin acción). Todo pasa por
 * `/api/admin/moderation/*`, gated en el servidor por `ManageContent` (403 sin
 * grant) — este panel solo es la superficie.
 */
export function ModerationQueue() {
  const { t } = useI18n();
  const [filter, setFilter] = createSignal<StatusFilter>("pending");

  const query = createApiQuery<ReportListResponse, ErrorModel>(
    () => ["admin", "moderation", "reports", filter()],
    async () =>
      api.GET("/api/admin/moderation/reports", { params: { query: { status: filter() } } }),
    () => ({ staleTime: 10_000 }),
  );

  const resolveMutation = createApiMutation(
    async (vars: { id: string; status: "resolved" | "dismissed"; resolution?: string }) =>
      api.POST("/api/admin/moderation/reports/{id}/resolve", {
        params: { path: { id: vars.id } },
        body: { status: vars.status, resolution: vars.resolution ?? null },
      }),
  );

  const act = async (report: Report, status: "resolved" | "dismissed") => {
    try {
      await resolveMutation.mutateAsync({ id: report.id, status });
      showToast(
        status === "resolved" ? t(msg`Report resolved.`) : t(msg`Report dismissed.`),
        "success",
      );
      await query.refetch();
    } catch {
      showToast(t(msg`Couldn't update the report.`), "error");
    }
  };

  const reasonLabel = (reason: string) => {
    switch (reason) {
      case "inappropriate": {
        return t(msg`Inappropriate`);
      }
      case "spam": {
        return t(msg`Spam`);
      }
      case "copyright": {
        return t(msg`Copyright`);
      }
      default: {
        return t(msg`Other`);
      }
    }
  };

  const filterLabel = (f: StatusFilter) =>
    f === "pending" ? t(msg`Pending`) : f === "resolved" ? t(msg`Resolved`) : t(msg`Dismissed`);

  return (
    <div class="space-y-6">
      <div class="flex flex-wrap items-end justify-between gap-4">
        <div class="flex items-center gap-3">
          <span class="grid h-11 w-11 place-items-center rounded-xl bg-accent/15 text-accent ring-1 ring-accent/20">
            <ShieldCheck class="h-6 w-6" />
          </span>
          <div>
            <h2 class="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              {t(msg`Warden's Cabinet`)}
            </h2>
            <p class="text-sm text-muted-foreground">
              {t(msg`Review reported community content.`)}
            </p>
          </div>
        </div>
        <Show when={query.data}>
          {(data) => {
            const { pending } = data();
            return (
              <span class="rounded-full bg-warning/15 px-3 py-1 text-sm font-semibold text-warning ring-1 ring-warning/20">
                {t(msg`${pending} pending`)}
              </span>
            );
          }}
        </Show>
      </div>

      {/* Status filter */}
      <div class="flex flex-wrap gap-2" role="tablist" aria-label={t(msg`Filter reports`)}>
        <For each={FILTERS}>
          {(f) => (
            <button
              type="button"
              role="tab"
              aria-selected={filter() === f}
              onClick={() => setFilter(f)}
              class="rounded-full px-4 py-1.5 text-sm font-medium ring-1 transition-colors"
              classList={{
                "bg-accent text-accent-foreground ring-accent": filter() === f,
                "bg-card text-muted-foreground ring-border hover:text-foreground": filter() !== f,
              }}
            >
              {filterLabel(f)}
            </button>
          )}
        </For>
      </div>

      <Show
        when={!query.isLoading}
        fallback={
          <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <For each={Array.from({ length: 6 }, () => 0)}>
              {() => <Skeleton class="h-52 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <Show
          when={(query.data?.data.length ?? 0) > 0}
          fallback={
            <EmptyState
              icon={Inbox}
              title={t(msg`Nothing to review`)}
              description={t(msg`The queue is calm. Reported content will appear here.`)}
            />
          }
        >
          <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <For each={query.data?.data ?? []}>
              {(report) => (
                <Card class="flex flex-col">
                  <CardContent class="flex flex-1 flex-col gap-3 p-5">
                    <div class="flex items-center justify-between gap-2">
                      <span class="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-semibold text-destructive ring-1 ring-destructive/20">
                        <Flag class="h-3.5 w-3.5" />
                        {reasonLabel(report.reason)}
                      </span>
                      <span class="text-xs uppercase tracking-wide text-muted-foreground">
                        {report.targetType}
                      </span>
                    </div>
                    <p class="text-sm text-foreground">
                      {report.details || t(msg`No additional details provided.`)}
                    </p>
                    <p class="text-xs text-muted-foreground">
                      {t(msg`Target`)}: <span class="font-mono">{report.targetId}</span>
                    </p>
                    <Show when={report.status !== "pending"}>
                      <p class="text-xs font-medium text-muted-foreground">
                        {report.status === "resolved" ? t(msg`Resolved`) : t(msg`Dismissed`)}
                        <Show when={report.resolution}>{` · ${report.resolution}`}</Show>
                      </p>
                    </Show>
                    <Show when={report.status === "pending"}>
                      <div class="mt-auto flex gap-2 border-t border-border/70 pt-3">
                        <Button
                          class="flex-1"
                          disabled={resolveMutation.isPending}
                          onClick={() => act(report, "resolved")}
                        >
                          <Check class="mr-1 h-4 w-4" />
                          {t(msg`Resolve`)}
                        </Button>
                        <Button
                          variant="outline"
                          class="flex-1"
                          disabled={resolveMutation.isPending}
                          onClick={() => act(report, "dismissed")}
                        >
                          <X class="mr-1 h-4 w-4" />
                          {t(msg`Dismiss`)}
                        </Button>
                      </div>
                    </Show>
                  </CardContent>
                </Card>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
